import { useEffect, useMemo, useState } from "react";
import { fetchAssets } from "../lib/settingsApi";

/**
 * Small modal that lists all Asset Library images and calls `onPick(url)`
 * with the chosen image's proxied URL when the admin taps one. Designed
 * to sit next to the src/poster text inputs in the Edit panel so the
 * admin can pick an existing asset instead of typing/pasting a URL.
 *
 * Kept separate from AssetLibrary.jsx (the full-page tab) because the
 * responsibilities are different: this is a picker (read-only, dismisses
 * on selection); the library is a manager (upload/delete/preview).
 */
export default function AssetPicker({ token, open, onClose, onPick }) {
  const [assets, setAssets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");

  // Refetch every time the picker opens so newly-uploaded assets appear
  // without a full page reload.
  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    fetchAssets(token)
      .then((list) => {
        setAssets(list || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, token]);

  // Document-level ESC listener — same rationale as AssetLibrary's
  // preview modal: the overlay div's onKeyDown never fires because the
  // grid buttons steal focus, so bind on document while `open`.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) => (a.filename || "").toLowerCase().includes(q));
  }, [assets, query]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="asset-picker-modal"
      className="fixed inset-0 z-[85] bg-ink/85 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      tabIndex={-1}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-paper text-ink max-w-5xl w-full max-h-[90vh] rounded-[2px] flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink/10">
          <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/60">
            Pick from asset library
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="asset-picker-close"
            className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/60 hover:text-ink"
          >
            Cancel
          </button>
        </div>

        {/* Search */}
        <div className="px-5 pt-4">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by filename…"
            data-testid="asset-picker-search"
            className="w-full px-4 py-2.5 font-sans text-[13px] border border-ink/20 focus:border-ink outline-none bg-paper"
          />
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {!loaded ? (
            <div className="font-sans text-[13px] text-ink/50">Loading library…</div>
          ) : filtered.length === 0 ? (
            <div className="font-sans text-[13px] text-ink/50 py-10 text-center">
              {query.trim() ? (
                <>No images match &ldquo;{query}&rdquo;.</>
              ) : (
                <>
                  No images in the library yet. Upload some from the{" "}
                  <em className="not-italic text-ink/80">Asset Library</em> tab first.
                </>
              )}
            </div>
          ) : (
            <ul
              data-testid="asset-picker-grid"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
            >
              {filtered.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => {
                      // Pass the proxied /api/media/{id} URL — same format
                      // upload_media returns, keeps the DB rows uniform.
                      onPick(a.url);
                      onClose();
                    }}
                    data-testid={`asset-picker-item-${a.id}`}
                    className="w-full text-left group"
                  >
                    <div className="aspect-square overflow-hidden bg-ink/5 border border-ink/10 group-hover:border-ink transition-colors">
                      <img
                        src={a.url}
                        alt={a.filename}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="mt-1.5 font-sans text-[11px] text-ink truncate" title={a.filename}>
                      {a.filename}
                    </div>
                    <div className="font-sans text-[10px] tracking-[0.18em] uppercase text-ink/50">
                      {a.width && a.height ? `${a.width}×${a.height}` : "— × —"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
